// src/components/popups/BarterFiSurveyScreen.js

import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';

import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import languageSwitcherIcon from '../../assets/languageSwitcher-icon.png';

import './BarterFiSurveyScreen.css';

const EMPTY_ANSWERS = {
  overallExperience: null,
  primaryUse: null,
  postingBarrier: null,
  stakingStatus: null,
  nonStakingReason: null,
};

const SURVEY_QUESTIONS = {
  overallExperience: {
    translationKey:
      'barterfi_survey.questions.overall_experience',
    options: [
      {
        value: 'excellent',
        translationKey: 'excellent',
        icon: '✨',
      },
      {
        value: 'good',
        translationKey: 'good',
        icon: '😊',
      },
      {
        value: 'okay',
        translationKey: 'okay',
        icon: '🙂',
      },
      {
        value: 'poor',
        translationKey: 'poor',
        icon: '😕',
      },
      {
        value: 'very_poor',
        translationKey: 'very_poor',
        icon: '☹️',
      },
    ],
  },

  primaryUse: {
    translationKey:
      'barterfi_survey.questions.primary_use',
    options: [
      {
        value: 'browse_posts',
        translationKey: 'browse_posts',
        icon: '👀',
      },
      {
        value: 'share_posts',
        translationKey: 'share_posts',
        icon: '✍️',
      },
      {
        value: 'pi_news',
        translationKey: 'pi_news',
        icon: '📰',
      },
      {
        value: 'explore_marketplace',
        translationKey: 'explore_marketplace',
        icon: '🛍️',
      },
      {
        value: 'connect_with_pioneers',
        translationKey:
          'connect_with_pioneers',
        icon: '🤝',
      },
      {
        value: 'other',
        translationKey: 'other',
        icon: '💭',
      },
    ],
  },

  postingBarrier: {
    translationKey:
      'barterfi_survey.questions.posting_barrier',
    options: [
      {
        value: 'more_active_discussions',
        translationKey:
          'more_active_discussions',
        icon: '💬',
      },
      {
        value: 'more_engagement',
        translationKey: 'more_engagement',
        icon: '❤️',
      },
      {
        value: 'content_ideas',
        translationKey: 'content_ideas',
        icon: '💡',
      },
      {
        value: 'relevant_connections',
        translationKey:
          'relevant_connections',
        icon: '🌐',
      },
      {
        value: 'already_post_regularly',
        translationKey:
          'already_post_regularly',
        icon: '🔥',
      },
      {
        value: 'prefer_reading',
        translationKey: 'prefer_reading',
        icon: '📖',
      },
    ],
  },

  stakingStatus: {
    translationKey:
      'barterfi_survey.questions.staking_status',
    options: [
      {
        value: 'yes',
        translationKey: 'yes',
        icon: '✓',
      },
      {
        value: 'no',
        translationKey: 'no',
        icon: '○',
      },
      {
        value: 'not_sure',
        translationKey: 'not_sure',
        icon: '?',
      },
    ],
  },

  nonStakingReason: {
    translationKey:
      'barterfi_survey.questions.non_staking_reason',
    options: [
      {
        value: 'willing_to_stake',
        translationKey:
          'willing_to_stake',
        icon: '🚀',
      },
      {
        value: 'barterfi_must_improve',
        translationKey:
          'barterfi_must_improve',
        icon: '🛠️',
      },
      {
        value: 'not_enough_pi',
        translationKey: 'not_enough_pi',
        icon: 'π',
      },
      {
        value: 'staking_other_apps',
        translationKey:
          'staking_other_apps',
        icon: '📱',
      },
      {
        value:
          'stake_wont_make_difference',
        translationKey:
          'stake_wont_make_difference',
        icon: '⚖️',
      },
      {
        value:
          'needs_more_information',
        translationKey:
          'needs_more_information',
        icon: '💡',
      },
      {
        value: 'do_not_want_to_stake',
        translationKey:
          'do_not_want_to_stake',
        icon: '—',
      },
    ],
  },

  optionalFeedback: {
    translationKey:
      'barterfi_survey.questions.optional_feedback',
  },
};

function BarterFiSurveyScreen({
  open = false,
  submitting = false,
  dismissing = false,
  openingStakeLink = false,
  errorMessage = '',
  result = null,
  onDismiss,
  onSubmit,
  onStakeClick,
}) {
  const { t } = useTranslation();


  const [screen, setScreen] =
    useState('intro');

const [
  showLanguageSwitcher,
  setShowLanguageSwitcher,
] = useState(false);

  const [currentStep, setCurrentStep] =
    useState(0);

  const [answers, setAnswers] =
    useState(EMPTY_ANSWERS);

  const [
    optionalFeedback,
    setOptionalFeedback,
  ] = useState('');

  const questionKeys = useMemo(() => {
    const keys = [
      'overallExperience',
      'primaryUse',
      'postingBarrier',
      'stakingStatus',
    ];

    if (answers.stakingStatus === 'no') {
      keys.push('nonStakingReason');
    }

    keys.push('optionalFeedback');

    return keys;
  }, [answers.stakingStatus]);

  const currentQuestionKey =
    questionKeys[currentStep];

  const currentQuestion =
    SURVEY_QUESTIONS[currentQuestionKey];

  const currentQuestionTranslationKey =
    currentQuestion?.translationKey;

  const isFeedbackStep =
    currentQuestionKey ===
    'optionalFeedback';

  const selectedValue =
    isFeedbackStep
      ? optionalFeedback
      : answers[currentQuestionKey];

  const isBusy =
    submitting ||
    dismissing ||
    openingStakeLink;

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      'hidden';

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [open]);



useEffect(() => {
  if (!open) {
    setScreen('intro');
    setCurrentStep(0);
    setAnswers(EMPTY_ANSWERS);
    setOptionalFeedback('');
    setShowLanguageSwitcher(false);
  }
}, [open]);


  useEffect(() => {
    if (result) {
      setScreen('result');
    }
  }, [result]);

  if (!open) {
    return null;
  }

  const handleDismiss = () => {
    if (isBusy || screen === 'result') {
      return;
    }

    onDismiss?.();
  };



const handleStart = () => {
  if (isBusy) {
    return;
  }

  setShowLanguageSwitcher(false);
  setCurrentStep(0);
  setScreen('questions');
};


  const handleSelectAnswer = (
    value
  ) => {
    if (
      isBusy ||
      isFeedbackStep
    ) {
      return;
    }

    setAnswers((current) => {
      const next = {
        ...current,
        [currentQuestionKey]: value,
      };

      if (
        currentQuestionKey ===
          'stakingStatus' &&
        value !== 'no'
      ) {
        next.nonStakingReason = null;
      }

      return next;
    });
  };

  const handleBack = () => {
    if (isBusy) {
      return;
    }

    if (currentStep === 0) {
      setScreen('intro');
      return;
    }

    setCurrentStep(
      (current) => current - 1
    );
  };

  const handleContinue = async () => {
    if (isBusy) {
      return;
    }

    if (
      !isFeedbackStep &&
      !selectedValue
    ) {
      return;
    }

    const isLastStep =
      currentStep ===
      questionKeys.length - 1;

    if (!isLastStep) {
      setCurrentStep(
        (current) => current + 1
      );

      return;
    }

    await onSubmit?.({
      answers: {
        overallExperience:
          answers.overallExperience,

        primaryUse:
          answers.primaryUse,

        postingBarrier:
          answers.postingBarrier,

        stakingStatus:
          answers.stakingStatus,

        nonStakingReason:
          answers.stakingStatus === 'no'
            ? answers.nonStakingReason
            : null,
      },

      optionalFeedback:
        optionalFeedback.trim() ||
        null,
    });
  };

  const getResultContent = () => {
    if (
      result?.stakeInvitationType ===
      'ready_to_stake'
    ) {
      return {
        icon: '🚀',
        translationKey:
          'barterfi_survey.results.ready_to_stake',
        showStakeButton: true,
      };
    }

    if (
      result?.stakeInvitationType ===
      'needs_information'
    ) {
      return {
        icon: '💡',
        translationKey:
          'barterfi_survey.results.needs_information',
        showStakeButton: true,
      };
    }

    if (
      answers.stakingStatus === 'yes'
    ) {
      return {
        icon: '💙',
        translationKey:
          'barterfi_survey.results.already_staked',
        showStakeButton: false,
      };
    }

    return {
      icon: '✓',
      translationKey:
        'barterfi_survey.results.default',
      showStakeButton: false,
    };
  };

  const resultContent =
    getResultContent();

  const resultTranslationKey =
    resultContent.translationKey;

  return (
    <div className="barterfi-survey-screen">
      <div className="barterfi-survey-screen-bg barterfi-survey-screen-bg--top" />

      <div className="barterfi-survey-screen-bg barterfi-survey-screen-bg--bottom" />

      {errorMessage && (
        <div className="barterfi-survey-screen-error">
          {errorMessage}
        </div>
      )}

{screen === 'intro' && (
  <div className="barterfi-survey-language-control">
    <button
      type="button"
      className="barterfi-survey-language-button"
      onClick={() =>
        setShowLanguageSwitcher(
          (current) => !current
        )
      }
      disabled={isBusy}
      aria-label="Change language"
      aria-expanded={showLanguageSwitcher}
    >
      <img
        src={languageSwitcherIcon}
        alt=""
        className="barterfi-survey-language-icon"
      />
    </button>

    {showLanguageSwitcher && (
      <div className="barterfi-survey-language-menu">
        <LanguageSwitcher
          compact
          showLabel={false}
        />
      </div>
    )}
  </div>
)}


      {screen !== 'result' && (
        <button
          type="button"
          className="barterfi-survey-screen-close"
          onClick={handleDismiss}
          disabled={isBusy}
          aria-label={t(
            'barterfi_survey.accessibility.close'
          )}
        >
          ×
        </button>
      )}

      {screen === 'intro' && (
        <main className="barterfi-survey-intro">
          <div className="barterfi-survey-intro-visual">
            <div className="barterfi-survey-intro-orbit barterfi-survey-intro-orbit--one" />

            <div className="barterfi-survey-intro-orbit barterfi-survey-intro-orbit--two" />

            <div className="barterfi-survey-intro-card">
              <span className="barterfi-survey-intro-card-icon">
                ✦
              </span>

              <span className="barterfi-survey-intro-card-label">
                BarterFi
              </span>
            </div>

            <span className="barterfi-survey-intro-float barterfi-survey-intro-float--one">
              💬
            </span>

            <span className="barterfi-survey-intro-float barterfi-survey-intro-float--two">
              π
            </span>

            <span className="barterfi-survey-intro-float barterfi-survey-intro-float--three">
              ✨
            </span>
          </div>

          <div className="barterfi-survey-intro-copy">
            <div className="barterfi-survey-eyebrow">
              {t(
                'barterfi_survey.intro.eyebrow'
              )}
            </div>

            <h1 className="barterfi-survey-intro-title">
              {t(
                'barterfi_survey.intro.title_line_1'
              )}

              <br />

              {t(
                'barterfi_survey.intro.title_line_2'
              )}
            </h1>

            <p className="barterfi-survey-intro-description">
              {t(
                'barterfi_survey.intro.description'
              )}
            </p>

            <div className="barterfi-survey-intro-meta">
              <span>
                {t(
                  'barterfi_survey.intro.duration'
                )}
              </span>

              <span>•</span>

              <span>
                {t(
                  'barterfi_survey.intro.honest_answers'
                )}
              </span>
            </div>
          </div>

          <div className="barterfi-survey-intro-actions">
            <button
              type="button"
              className="barterfi-survey-primary-button"
              onClick={handleStart}
              disabled={isBusy}
            >
              {t(
                'barterfi_survey.buttons.start'
              )}
            </button>

            <button
              type="button"
              className="barterfi-survey-secondary-button"
              onClick={handleDismiss}
              disabled={isBusy}
            >
              {dismissing
                ? t(
                    'barterfi_survey.buttons.closing'
                  )
                : t(
                    'barterfi_survey.buttons.not_now'
                  )}
            </button>
          </div>
        </main>
      )}

      {screen === 'questions' && (
        <main className="barterfi-survey-question-screen">
          <header className="barterfi-survey-question-header">
            <button
              type="button"
              className="barterfi-survey-back-button"
              onClick={handleBack}
              disabled={isBusy}
              aria-label={t(
                'barterfi_survey.accessibility.previous_question'
              )}
            >
              ‹
            </button>

            <div className="barterfi-survey-progress-copy">
              {t(
                'barterfi_survey.progress',
                {
                  current:
                    currentStep + 1,
                  total:
                    questionKeys.length,
                }
              )}
            </div>
          </header>

          <div className="barterfi-survey-progress-track">
            <div
              className="barterfi-survey-progress-value"
              style={{
                width: `${
                  (
                    (currentStep + 1) /
                    questionKeys.length
                  ) * 100
                }%`,
              }}
            />
          </div>

          <section className="barterfi-survey-question-content">
            <div className="barterfi-survey-eyebrow">
              {t(
                `${currentQuestionTranslationKey}.eyebrow`
              )}
            </div>

            <h1 className="barterfi-survey-question-title">
              {t(
                `${currentQuestionTranslationKey}.question`
              )}
            </h1>

            <p className="barterfi-survey-question-description">
              {t(
                `${currentQuestionTranslationKey}.description`
              )}
            </p>

            {!isFeedbackStep && (
              <div className="barterfi-survey-options">
                {currentQuestion.options.map(
                  (option) => {
                    const selected =
                      selectedValue ===
                      option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={
                          selected
                            ? 'barterfi-survey-option selected'
                            : 'barterfi-survey-option'
                        }
                        onClick={() =>
                          handleSelectAnswer(
                            option.value
                          )
                        }
                        disabled={isBusy}
                      >
                        <span className="barterfi-survey-option-icon">
                          {option.icon}
                        </span>

                        <span className="barterfi-survey-option-label">
                          {t(
                            `${currentQuestionTranslationKey}.options.${option.translationKey}`
                          )}
                        </span>

                        <span className="barterfi-survey-option-check">
                          {selected
                            ? '✓'
                            : ''}
                        </span>
                      </button>
                    );
                  }
                )}
              </div>
            )}

            {isFeedbackStep && (
              <div className="barterfi-survey-feedback-wrap">
                <textarea
                  className="barterfi-survey-feedback"
                  value={optionalFeedback}
                  onChange={(event) =>
                    setOptionalFeedback(
                      event.target.value.slice(
                        0,
                        1000
                      )
                    )
                  }
                  placeholder={t(
                    'barterfi_survey.feedback.placeholder'
                  )}
                  maxLength={1000}
                  disabled={isBusy}
                />

                <div className="barterfi-survey-feedback-count">
                  {optionalFeedback.length}
                  /1000
                </div>
              </div>
            )}
          </section>

          <footer className="barterfi-survey-question-footer">
            <button
              type="button"
              className="barterfi-survey-primary-button"
              onClick={handleContinue}
              disabled={
                isBusy ||
                (
                  !isFeedbackStep &&
                  !selectedValue
                )
              }
            >
              {submitting
                ? t(
                    'barterfi_survey.buttons.submitting'
                  )
                : currentStep ===
                    questionKeys.length - 1
                  ? t(
                      'barterfi_survey.buttons.submit'
                    )
                  : t(
                      'barterfi_survey.buttons.continue'
                    )}
            </button>

            {isFeedbackStep && (
              <div className="barterfi-survey-footer-note">
                {t(
                  'barterfi_survey.feedback.optional_note'
                )}
              </div>
            )}
          </footer>
        </main>
      )}

      {screen === 'result' && (
        <main className="barterfi-survey-result">
          <div className="barterfi-survey-result-icon">
            {resultContent.icon}
          </div>

          <div className="barterfi-survey-eyebrow">
            {t(
              `${resultTranslationKey}.eyebrow`
            )}
          </div>

          <h1 className="barterfi-survey-result-title">
            {t(
              `${resultTranslationKey}.title`
            )}
          </h1>

          <p className="barterfi-survey-result-description">
            {t(
              `${resultTranslationKey}.description`
            )}
          </p>

          <div className="barterfi-survey-result-card">
            <span className="barterfi-survey-result-card-icon">
              ✓
            </span>

            <div>
              <strong>
                {t(
                  'barterfi_survey.completion.title'
                )}
              </strong>

              <span>
                {t(
                  'barterfi_survey.completion.description'
                )}
              </span>
            </div>
          </div>

          <div className="barterfi-survey-result-actions">
            {resultContent.showStakeButton && (
              <button
                type="button"
                className="barterfi-survey-primary-button"
                onClick={onStakeClick}
                disabled={isBusy}
              >
                {openingStakeLink
                  ? t(
                      'barterfi_survey.buttons.opening'
                    )
                  : t(
                      `${resultTranslationKey}.stake_button`
                    )}
              </button>
            )}

            <button
              type="button"
              className={
                resultContent.showStakeButton
                  ? 'barterfi-survey-secondary-button'
                  : 'barterfi-survey-primary-button'
              }
              onClick={() =>
                onDismiss?.({
                  completed: true,
                })
              }
              disabled={isBusy}
            >
              {t(
                'barterfi_survey.buttons.done'
              )}
            </button>
          </div>

          {resultContent.showStakeButton && (
            <p className="barterfi-survey-stake-note">
              {t(
                'barterfi_survey.staking_note'
              )}
            </p>
          )}
        </main>
      )}
    </div>
  );
}

export default BarterFiSurveyScreen;
